// =============================================================================
// pmoShared — the PMO client governance dashboard's types, hooks and bits
// =============================================================================
// 22 Oct 2026 (mig 446). James's brief: a clean, client-specific dashboard he
// presents in recurring client meetings (Ventia, APD) instead of walking the
// client through Ops. Everything here reads the client-safe views
// (v_pmo_clients / v_pmo_projects / v_pmo_bids / v_pmo_hse) — no cost, no
// margin, no other client — so the screens cannot leak by accident.
//
// Dates edited in a meeting write to the project's own columns (one field,
// no copy). The "note for today" per site lives in pmo_project_notes and can
// be kept on the project with one tap (app.pmo_keep_note_on_project).
// =============================================================================

import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Tone } from "@/components/ui";

export interface PmoClient {
  client_id: string; client_code: string; legal_name: string; trading_name: string | null; pmo_enabled: boolean;
  carriers: string | null;
  live_count: number; upcoming_count: number; acceptance_count: number; done_this_month: number;
  open_defects: number; overdue_defects: number; hop_outstanding: number;
  bids_open: number; bids_responded: number; bids_won_12m: number; bids_lost_12m: number;
  actions_open: number; actions_overdue: number;
  last_meeting_date: string | null; open_meeting_id: string | null;
}

export type Bucket = "live" | "upcoming" | "acceptance" | "done" | "other";
export type ReadyState = "ready" | "partial" | "pending" | "missing" | "none" | "na";

export interface PmoProject {
  project_id: string; client_id: string; project_code: string; project_name: string; status: string; bucket: Bucket;
  site_code: string | null; site_name: string | null; suburb: string | null; state: string | null; carrier: string | null;
  project_type: string | null; client_job_number: string | null; client_project_ref: string | null; client_po_number: string | null; client_po_value: number | null; pow_id: string | null;
  scheduled_start_date: string | null; scheduled_end_date: string | null; actual_start_date: string | null; actual_end_date: string | null; acceptance_received_at: string | null;
  open_defects: number; overdue_defects: number;
  hop_status: string | null; hop_submitted_at: string | null; hop_accepted_at: string | null;
  last_diary_date: string | null; diary_entries: number;
  design_state: ReadyState; design_why: string; materials_state: ReadyState; materials_why: string;
  outage_state: ReadyState; outage_why: string; plant_state: ReadyState; plant_why: string; access_state: ReadyState; access_why: string;
  po_state?: ReadyState; po_why?: string;   // mig 466 — the client's PO / STOW against the contract
  /** mig 467 — squares set by hand in the meeting, merged in by applyReadinessOverrides */
  ready_overrides?: Partial<Record<ReadyKey, ReadinessOverride>>;
  ready_system?: Partial<Record<ReadyKey, { state: ReadyState; why: string }>>;
  standdown_blocks: boolean; next_milestone: string | null; next_milestone_date: string | null;
  pmo_note: string | null; pmo_note_at: string | null;
  /** mig 491 — the hub as a control tool */
  pmo_flag?: string | null; pmo_flag_at?: string | null; pmo_flag_by?: string | null;
  hop_outside_ops?: boolean;
  days_in_acceptance?: number | null;   // acceptance_pending: days since the build finished
  acceptance_days?: number | null;      // closed: build done → acceptance received (the KPI)
  date_clash?: string | null;           // read off the dates: past its end and still in build, late start, end before start
}

export interface PmoBid {
  bid_id: string; client_id: string; bid_number: string | null; bid_title: string; status: string; received_date: string | null; due_date: string | null;
  decision_due_date: string | null; response_sent_at: string | null; estimated_value: number | null; client_job_number: string | null;
  promoted_to_project_id: string | null; promoted_at: string | null; site_code: string | null; site_name: string | null; suburb: string | null; state: string | null; carrier: string | null;
}

export interface PmoHse {
  client_id: string; alerts_90d: number; alerts_urgent: number; ack_targeted: number; ack_done: number; ack_avg_days: number | null;
  incidents_90d: number; incidents_open: number; incidents_serious: number; corrective_open: number; standdowns_active: number;
}

export interface PmoMeeting {
  meeting_id: string; client_id: string; meeting_date: string; title: string; attendees: { name: string; org: string }[];
  pre_notes: string | null; notes: string | null; decisions: string | null; status: "open" | "finished"; finished_at: string | null; created_at: string;
}

export interface PmoAction {
  action_id: string; client_id: string; project_id: string | null; raised_in_meeting_id: string | null; title: string; details: string | null;
  owner_kind: "coretel" | "client"; owner_employee_id: string | null; owner_name: string | null; due_date: string | null;
  status: "open" | "done" | "parked"; done_at: string | null; done_in_meeting_id: string | null; done_note: string | null; created_at: string;
  owner?: { first_name: string | null; last_name: string | null } | null;
  project?: { project_code: string; project_name: string } | null;
}

export const BUCKET_LABEL: Record<Bucket, string> = { live: "Live", upcoming: "Upcoming", acceptance: "Awaiting acceptance", done: "Completed", other: "Other" };
export const STATUS_TONE: Record<string, Tone> = { live: "live", on_hold: "on_hold", mobilising: "mobilising", scheduled: "mobilising", won: "won", acceptance_pending: "acceptance_pending", closed: "closed" };
export const HOP_LABEL: Record<string, string> = {
  open: "pack open", submitted: "submitted", accepted: "accepted", defects_raised: "defects raised", submitted_to_manager: "with Coretel",
  manager_review_changes_needed: "with Coretel", submitted_to_client: "with client", client_changes_needed: "client wants changes", client_accepted: "accepted", closed: "closed",
};
export const HOP_OUTSTANDING = new Set(["submitted", "submitted_to_client", "client_changes_needed", "defects_raised"]);
/** the pack is with the client (sent, waiting on them) */
export const HOP_WITH_CLIENT = new Set(["submitted", "submitted_to_client", "client_changes_needed"]);
/** the pack has been accepted / closed out */
export const HOP_DONE = new Set(["accepted", "client_accepted", "closed"]);

// -----------------------------------------------------------------------------
// 2 Nov 2026 (mig 491) — the lane colour. James: "Red would be in build (Live)
// and either RAG not good or a flagged issue like date clash. Acceptance
// pending … should not be red, that could take months … yellow if defects
// found or no HOP sent (overridable if HOP is not done in Ops), green for
// defect free." One function, one answer per row, with the reasons.
// -----------------------------------------------------------------------------
export type LaneTone = "red" | "amber" | "blue" | "green" | "grey";
export const LANE_CLS: Record<LaneTone, { bar: string; bg: string; text: string; label: string }> = {
  red:   { bar: "bg-danger", bg: "bg-danger/[0.06]", text: "text-danger", label: "needs a word" },
  amber: { bar: "bg-warn",   bg: "bg-warn/[0.06]",   text: "text-warn",   label: "watch" },
  blue:  { bar: "bg-info",   bg: "bg-info/[0.05]",   text: "text-info",   label: "with the client" },
  green: { bar: "bg-ok",     bg: "",                 text: "text-ok",     label: "clean" },
  grey:  { bar: "bg-line",   bg: "",                 text: "text-text-dim", label: "" },
};
const READY_KEYS_RED: ReadyKey[] = ["design", "materials", "access", "po"];   // missing on a job in build is red; outage / plant are n/a on most jobs
export function laneState(p: PmoProject): { tone: LaneTone; why: string[] } {
  const why: string[] = [];
  const st = (k: ReadyKey): ReadyState => (p as any)[`${k}_state`] ?? "na";
  const hopOut = !!p.hop_status && HOP_OUTSTANDING.has(p.hop_status);
  if (p.bucket === "live") {
    if (p.standdown_blocks) why.push("Stood down — HSE");
    if (p.pmo_flag) why.push(`Flagged: ${p.pmo_flag}`);
    if (p.date_clash) why.push(p.date_clash);
    for (const k of READY_KEYS_RED) if (st(k) === "missing") why.push(`${READY_KEYS.find(r => r.key === k)?.label ?? k} missing`);
    if (p.overdue_defects > 0) why.push(`${p.overdue_defects} overdue defect${p.overdue_defects === 1 ? "" : "s"}`);
    if (why.length) return { tone: "red", why };
    if (p.status === "on_hold") why.push("On hold");
    for (const k of READY_KEYS) if (st(k.key) === "partial" || st(k.key) === "pending") why.push(`${k.label} ${st(k.key)}`);
    if (p.open_defects > 0) why.push(`${p.open_defects} defect${p.open_defects === 1 ? "" : "s"} open`);
    if (hopOut) why.push("HOP pack outstanding");
    return { tone: why.length ? "amber" : "green", why: why.length ? why : ["In build, nothing waiting"] };
  }
  if (p.bucket === "upcoming") {
    if (p.standdown_blocks) why.push("Stood down — HSE");
    if (p.pmo_flag) why.push(`Flagged: ${p.pmo_flag}`);
    if (p.date_clash) why.push(p.date_clash);
    if (why.length) return { tone: "red", why };
    const soon = daysUntil(p.scheduled_start_date); const close = soon != null && soon <= 14;
    for (const k of READY_KEYS_RED) if (st(k) === "missing" && close) why.push(`${READY_KEYS.find(r => r.key === k)?.label ?? k} missing, starts ${fmtD(p.scheduled_start_date)}`);
    for (const k of READY_KEYS) if (st(k.key) === "partial" || st(k.key) === "pending") why.push(`${k.label} ${st(k.key)}`);
    return { tone: why.length ? "amber" : "green", why: why.length ? why : ["Scheduled, ready so far"] };
  }
  if (p.bucket === "acceptance") {
    // never red — this can take months and is tracked here on its own
    if (p.pmo_flag) why.push(`Flagged: ${p.pmo_flag}`);
    if (p.overdue_defects > 0) why.push(`${p.overdue_defects} overdue defect${p.overdue_defects === 1 ? "" : "s"}`);
    else if (p.open_defects > 0) why.push(`${p.open_defects} defect${p.open_defects === 1 ? "" : "s"} open`);
    const packDone = !!p.hop_status && HOP_DONE.has(p.hop_status);
    const packWithClient = !!p.hop_status && HOP_WITH_CLIENT.has(p.hop_status);
    if (!p.hop_outside_ops && !packDone && !packWithClient) why.push(p.hop_status ? `HOP pack ${HOP_LABEL[p.hop_status] ?? p.hop_status} — not sent yet` : "No HOP pack sent");
    if (why.length) return { tone: "amber", why };
    if (packWithClient) return { tone: "blue", why: [`HOP pack with the client${p.hop_submitted_at ? ` · ${daysSince(p.hop_submitted_at)} d` : ""}`] };
    return { tone: "green", why: [p.hop_outside_ops ? "Defect free · HOP pack handled outside Ops" : "Defect free · pack accepted"] };
  }
  return { tone: "grey", why: [] };
}

/** mig 491 — the status moves the table offers, from where the job sits. Closing asks for the acceptance date (projects_status_acceptance_ck). */
export const STATUS_NEXT: Record<string, { to: string; label: string }[]> = {
  won:                [{ to: "scheduled", label: "Scheduled" }, { to: "mobilising", label: "Mobilising" }, { to: "live", label: "Live — in build" }],
  scheduled:          [{ to: "mobilising", label: "Mobilising" }, { to: "live", label: "Live — in build" }, { to: "on_hold", label: "On hold" }],
  mobilising:         [{ to: "live", label: "Live — in build" }, { to: "scheduled", label: "Back to scheduled" }, { to: "on_hold", label: "On hold" }],
  live:               [{ to: "acceptance_pending", label: "Build done → acceptance pending" }, { to: "on_hold", label: "On hold" }, { to: "closed", label: "Closed (accepted)" }],
  on_hold:            [{ to: "live", label: "Back in build" }, { to: "acceptance_pending", label: "Acceptance pending" }],
  acceptance_pending: [{ to: "closed", label: "Closed — acceptance received" }, { to: "live", label: "Back in build (rework)" }],
  closed:             [{ to: "acceptance_pending", label: "Reopen → acceptance pending" }],
};
export const STATUS_LABEL: Record<string, string> = { won: "won", scheduled: "scheduled", mobilising: "mobilising", live: "live", on_hold: "on hold", acceptance_pending: "acceptance pending", closed: "closed" };

export function today(): string { const d = new Date(); const p = (n: number) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`; }
export function fmtD(key: string | null | undefined, withYear = false): string {
  if (!key) return "—";
  const [y, m, d] = key.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-AU", { day: "numeric", month: "short", ...(withYear ? { year: "numeric" } : {}) });
}
export function daysSince(iso: string | null | undefined): number | null { if (!iso) return null; return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000); }
export function daysUntil(key: string | null | undefined): number | null { if (!key) return null; const [y, m, d] = key.slice(0, 10).split("-").map(Number); return Math.round((new Date(y, m - 1, d).getTime() - new Date(today() + "T00:00:00").getTime()) / 86400000); }
export function clientName(c: { legal_name: string; trading_name: string | null }): string { return c.trading_name || c.legal_name; }

// ---- reads -------------------------------------------------------------------

export function usePmoClients() {
  return useQuery({ queryKey: ["pmo_clients"], queryFn: async () => {
    const { data, error } = await (supabase as any).from("v_pmo_clients").select("*").order("legal_name");
    if (error) throw error; return (data ?? []) as PmoClient[];
  } });
}
export function usePmoProjects(clientId: string | undefined) {
  return useQuery({ queryKey: ["pmo_projects", clientId], enabled: !!clientId, queryFn: async () => {
    const { data, error } = await (supabase as any).from("v_pmo_projects").select("*").eq("client_id", clientId!).order("scheduled_start_date", { ascending: true, nullsFirst: false });
    if (error) throw error; return (data ?? []) as PmoProject[];
  } });
}
export function usePmoBids(clientId: string | undefined) {
  return useQuery({ queryKey: ["pmo_bids", clientId], enabled: !!clientId, queryFn: async () => {
    const { data, error } = await (supabase as any).from("v_pmo_bids").select("*").eq("client_id", clientId!).order("received_date", { ascending: false }).limit(200);
    if (error) throw error; return (data ?? []) as PmoBid[];
  } });
}
export function usePmoHse(clientId: string | undefined) {
  return useQuery({ queryKey: ["pmo_hse", clientId], enabled: !!clientId, queryFn: async () => {
    const { data, error } = await (supabase as any).from("v_pmo_hse").select("*").eq("client_id", clientId!).maybeSingle();
    if (error) throw error; return (data ?? null) as PmoHse | null;
  } });
}
export function usePmoMeetings(clientId: string | undefined) {
  return useQuery({ queryKey: ["pmo_meetings", clientId], enabled: !!clientId, queryFn: async () => {
    const { data, error } = await (supabase as any).from("pmo_meetings").select("*").eq("client_id", clientId!).order("meeting_date", { ascending: false }).limit(50);
    if (error) throw error; return (data ?? []) as PmoMeeting[];
  } });
}
export function usePmoActions(clientId: string | undefined) {
  return useQuery({ queryKey: ["pmo_actions", clientId], enabled: !!clientId, queryFn: async () => {
    const { data, error } = await (supabase as any).from("pmo_actions")
      .select("*, owner:owner_employee_id(first_name, last_name), project:project_id(project_code, project_name)")
      .eq("client_id", clientId!).order("status").order("due_date", { ascending: true, nullsFirst: false }).limit(300);
    if (error) throw error; return (data ?? []) as PmoAction[];
  } });
}
export function useEmployeesList(enabled = true) {
  return useQuery({ queryKey: ["pmo_employees"], enabled, queryFn: async () => {
    const { data, error } = await supabase.from("employees").select("employee_id, first_name, last_name").eq("is_active", true).order("first_name");
    if (error) throw error; return (data ?? []) as { employee_id: string; first_name: string | null; last_name: string | null }[];
  } });
}

export function invalidatePmo(qc: ReturnType<typeof useQueryClient>, clientId?: string) {
  for (const k of ["pmo_clients", "pmo_projects", "pmo_bids", "pmo_hse", "pmo_meetings", "pmo_actions"]) qc.invalidateQueries({ queryKey: clientId ? [k, clientId] : [k] });
  qc.invalidateQueries({ queryKey: ["pmo_clients"] });
}

// ---- writes ------------------------------------------------------------------

export function usePmoWrites(clientId: string | undefined) {
  const qc = useQueryClient();
  const done = () => invalidatePmo(qc, clientId);
  const rpc = async (fn: string, args: Record<string, unknown>) => {
    const { data, error } = await (supabase as any).schema("app").rpc(fn, args);
    if (error) throw new Error(error.message); return data;
  };
  const table = async (name: string, op: (q: any) => any) => {
    const { data, error } = await op((supabase as any).from(name));
    if (error) throw new Error(error.message); return data;
  };
  const setPmoEnabled = useMutation({ mutationFn: (v: { client_id: string; on: boolean }) => table("clients", q => q.update({ pmo_enabled: v.on }).eq("client_id", v.client_id)), onSuccess: done });
  const startMeeting  = useMutation({ mutationFn: (v: { client_id: string; title?: string }) => table("pmo_meetings", q => q.insert({ client_id: v.client_id, title: v.title ?? "Governance meeting" }).select("meeting_id").single()), onSuccess: done });
  const saveMeeting   = useMutation({ mutationFn: (v: { meeting_id: string; patch: Partial<Pick<PmoMeeting, "title" | "meeting_date" | "attendees" | "pre_notes" | "notes" | "decisions">> }) => table("pmo_meetings", q => q.update(v.patch).eq("meeting_id", v.meeting_id)), onSuccess: () => qc.invalidateQueries({ queryKey: ["pmo_meetings", clientId] }) });
  const finishMeeting = useMutation({ mutationFn: (v: { meeting_id: string }) => rpc("pmo_finish_meeting", { p_meeting_id: v.meeting_id }), onSuccess: done });
  const addAction     = useMutation({ mutationFn: (v: { client_id: string; title: string; details?: string | null; project_id?: string | null; raised_in_meeting_id?: string | null; owner_kind: "coretel" | "client"; owner_employee_id?: string | null; owner_name?: string | null; due_date?: string | null }) =>
    table("pmo_actions", q => q.insert(v)), onSuccess: done });
  const setActionStatus = useMutation({ mutationFn: (v: { action_id: string; status: "open" | "done" | "parked"; meeting_id?: string | null; note?: string | null }) =>
    rpc("pmo_action_set_status", { p_action_id: v.action_id, p_status: v.status, p_meeting_id: v.meeting_id ?? null, p_note: v.note ?? null }), onSuccess: done });
  const saveNote      = useMutation({ mutationFn: (v: { project_id: string; note: string; meeting_id?: string | null }) => table("pmo_project_notes", q => q.upsert({ project_id: v.project_id, note: v.note, meeting_id: v.meeting_id ?? null }, { onConflict: "project_id" })), onSuccess: () => qc.invalidateQueries({ queryKey: ["pmo_projects", clientId] }) });
  const keepNote      = useMutation({ mutationFn: (v: { project_id: string; client_label: string }) => rpc("pmo_keep_note_on_project", { p_project_id: v.project_id, p_client_label: v.client_label }) });
  // dates edited in the meeting ARE the project's dates
  const saveProjectDates = useMutation({ mutationFn: (v: { project_id: string; patch: Partial<Pick<PmoProject, "scheduled_start_date" | "scheduled_end_date" | "actual_start_date" | "actual_end_date">> }) =>
    table("projects", q => q.update(v.patch).eq("project_id", v.project_id)), onSuccess: () => { qc.invalidateQueries({ queryKey: ["pmo_projects", clientId] }); qc.invalidateQueries({ queryKey: ["projects"] }); } });
  const closeDefectsRefresh = () => qc.invalidateQueries({ queryKey: ["pmo_projects", clientId] });
  // mig 491 — status changed from the table: the same write the project page's
  // "Change status" does (actual dates stamped, acceptance date on close), and
  // every list that filters on status told (the Yarraman lesson).
  const setProjectStatus = useMutation({
    mutationFn: async (v: { p: PmoProject; status: string; acceptanceDate?: string | null }) => {
      const t = today(); const patch: any = { status: v.status };
      if (v.status === "live" && !v.p.actual_start_date) patch.actual_start_date = t;
      if ((v.status === "acceptance_pending" || v.status === "closed") && !v.p.actual_end_date) patch.actual_end_date = t;
      if (v.status === "closed" && !v.p.acceptance_received_at) patch.acceptance_received_at = v.acceptanceDate || t;
      if (v.status === "live" && v.p.status === "acceptance_pending") patch.actual_end_date = null;   // back in build: the build is not done
      return table("projects", q => q.update(patch).eq("project_id", v.p.project_id));
    },
    onSuccess: () => { done(); for (const k of ["schedule_projects", "schedule_pcas", "projects", "project"]) qc.invalidateQueries({ queryKey: [k] }); },
  });
  const setFlag = useMutation({ mutationFn: (v: { project_id: string; reason: string | null; userId: string | null; userName: string | null }) =>
    table("pmo_project_notes", q => q.upsert({ project_id: v.project_id, flag_reason: v.reason, flagged_at: v.reason ? new Date().toISOString() : null, flagged_by: v.reason ? v.userId : null, flagged_by_name: v.reason ? v.userName : null }, { onConflict: "project_id" })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pmo_projects", clientId] }) });
  const setHopOutsideOps = useMutation({ mutationFn: (v: { project_id: string; on: boolean }) =>
    table("pmo_project_notes", q => q.upsert({ project_id: v.project_id, hop_outside_ops: v.on }, { onConflict: "project_id" })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pmo_projects", clientId] }) });
  return { setPmoEnabled, startMeeting, saveMeeting, finishMeeting, addAction, setActionStatus, saveNote, keepNote, saveProjectDates, closeDefectsRefresh, setProjectStatus, setFlag, setHopOutsideOps };
}

/** Debounced text that saves 800 ms after the last keystroke — live notes in a meeting. */
export function useAutosaveText(initial: string, save: (v: string) => void, deps: unknown[] = []) {
  const [value, setValue] = useState(initial);
  const [state, setState] = useState<"saved" | "typing" | "saving">("saved");
  const timer = useRef<number | null>(null);
  const last = useRef(initial);
  useEffect(() => { setValue(initial); last.current = initial; setState("saved"); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, deps);
  const onChange = (v: string) => {
    setValue(v); setState("typing");
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => { if (v !== last.current) { last.current = v; setState("saving"); save(v); window.setTimeout(() => setState("saved"), 400); } else setState("saved"); }, 800);
  };
  return { value, onChange, state };
}

// ---- readiness set in the meeting (mig 467) ----------------------------------
// James, 27 Oct 2026: "a nice clean way to update and change the RAG in real
// time, say in the meeting as we check things off." The squares stay READ
// from Ops; a hand-set state sits on top with who / when / why, and shows a
// marker. "Back to automatic" deletes the row.

export type ReadyKey = "design" | "materials" | "outage" | "plant" | "access" | "po";
export const READY_KEYS: { key: ReadyKey; label: string }[] = [
  { key: "design", label: "Design / FC" }, { key: "materials", label: "Materials" }, { key: "outage", label: "Outage" },
  { key: "plant", label: "Plant" }, { key: "access", label: "Access" }, { key: "po", label: "PO" },
];
export interface ReadinessOverride { project_id: string; ready_key: ReadyKey; state: ReadyState; note: string | null; meeting_id: string | null; set_by: string | null; set_by_name: string | null; set_at: string }

export function useReadinessOverrides(projectIds: string[]) {
  const key = projectIds.slice().sort().join(",");
  return useQuery({ queryKey: ["pmo_ready_overrides", key], enabled: projectIds.length > 0, queryFn: async () => {
    const { data, error } = await (supabase as any).from("pmo_readiness_overrides").select("*").in("project_id", projectIds);
    if (error) throw error; return (data ?? []) as ReadinessOverride[];
  } });
}

/** The project with the hand-set states laid over the computed ones; the computed answer kept in ready_system. */
export function applyReadinessOverrides(p: PmoProject, overrides: ReadinessOverride[]): PmoProject {
  const mine = overrides.filter(o => o.project_id === p.project_id);
  if (!mine.length) return p;
  const out: any = { ...p, ready_overrides: {}, ready_system: {} };
  for (const o of mine) {
    out.ready_system[o.ready_key] = { state: (p as any)[`${o.ready_key}_state`], why: (p as any)[`${o.ready_key}_why`] };
    out.ready_overrides[o.ready_key] = o;
    out[`${o.ready_key}_state`] = o.state;
    out[`${o.ready_key}_why`] = `Set in the meeting${o.set_by_name ? ` by ${o.set_by_name}` : ""} ${new Date(o.set_at).toLocaleDateString("en-AU", { day: "2-digit", month: "short" })}${o.note ? ` — ${o.note}` : ""}`;
  }
  return out as PmoProject;
}

export function useReadinessOverrideWrites(projectIds: string[]) {
  const qc = useQueryClient();
  const key = projectIds.slice().sort().join(",");
  const done = () => { qc.invalidateQueries({ queryKey: ["pmo_ready_overrides", key] }); };
  const set = useMutation({
    mutationFn: async (v: { project_id: string; ready_key: ReadyKey; state: ReadyState; note: string | null; meeting_id: string | null; set_by: string | null; set_by_name: string | null }) => {
      const { error } = await (supabase as any).from("pmo_readiness_overrides").upsert({ ...v, set_at: new Date().toISOString() }, { onConflict: "project_id,ready_key" });
      if (error) throw new Error(error.message);
    }, onSuccess: done,
  });
  const clear = useMutation({
    mutationFn: async (v: { project_id: string; ready_key: ReadyKey }) => {
      const { error } = await (supabase as any).from("pmo_readiness_overrides").delete().eq("project_id", v.project_id).eq("ready_key", v.ready_key);
      if (error) throw new Error(error.message);
    }, onSuccess: done,
  });
  return { set, clear };
}

/** The popover: what the system says, what was set, the four buttons, a note. */
export function ReadinessSetter({ p, k, meetingId, userId, userName, writes, onClose }: { p: PmoProject; k: ReadyKey; meetingId: string | null; userId: string | null; userName: string | null; writes: ReturnType<typeof useReadinessOverrideWrites>; onClose: () => void }) {
  const label = READY_KEYS.find(x => x.key === k)!.label;
  const ov = p.ready_overrides?.[k] ?? null;
  const sys = p.ready_system?.[k] ?? { state: (p as any)[`${k}_state`] as ReadyState, why: (p as any)[`${k}_why`] as string };
  const [note, setNote] = useState(ov?.note ?? "");
  const busy = writes.set.isPending || writes.clear.isPending;
  const pick = (state: ReadyState) => writes.set.mutate({ project_id: p.project_id, ready_key: k, state, note: note.trim() || null, meeting_id: meetingId, set_by: userId, set_by_name: userName }, { onSuccess: onClose });
  const btn = (state: ReadyState, text: string) => (
    <button type="button" disabled={busy} onClick={() => pick(state)}
      className={`flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-md border text-[12px] ${ov?.state === state ? "border-accent bg-accent/10" : "border-line hover:border-line-light"}`}>
      <i className={`w-2.5 h-2.5 rounded-sm ${READY_CLS[state]}`}/>{text}
    </button>
  );
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-t-2xl sm:rounded-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-line">
          <div className="text-[14px] font-semibold">{label} · {p.site_name ?? p.project_code}</div>
          <div className="mt-2 text-[12px] flex items-start gap-2"><i className={`mt-1 w-2.5 h-2.5 rounded-sm shrink-0 ${READY_CLS[sys.state]}`}/><span><span className="text-text-muted">Ops says {READY_LABEL[sys.state]}:</span> {sys.why}</span></div>
          {ov && <div className="mt-1.5 text-[12px] flex items-start gap-2"><i className={`mt-1 w-2.5 h-2.5 rounded-sm shrink-0 ${READY_CLS[ov.state]}`}/><span><span className="text-text-muted">Set in the meeting:</span> {READY_LABEL[ov.state]}{ov.set_by_name ? ` · ${ov.set_by_name}` : ""} · {new Date(ov.set_at).toLocaleDateString("en-AU", { day: "2-digit", month: "short" })}{ov.note ? ` — ${ov.note}` : ""}</span></div>}
        </div>
        <div className="p-4 space-y-3">
          <div className="text-[10px] uppercase tracking-wider2 text-text-muted">Set it as</div>
          <div className="flex gap-1.5">{btn("ready", "Ready")}{btn("partial", "Partial")}{btn("missing", "Missing")}{btn("na", "N/A")}</div>
          <input className="w-full bg-ink-raised border border-line rounded-md px-2.5 py-1.5 text-[12.5px] outline-none focus:border-accent" placeholder="why — e.g. 'FC received by email, filing Friday'" value={note} onChange={e => setNote(e.target.value)}/>
          <div className="text-[11px] text-text-dim">Picking a state saves it with the note. The square shows a marker while it is hand-set; Ops' own answer stays underneath.</div>
        </div>
        <div className="p-3 border-t border-line flex gap-2 items-center">
          {ov && <button type="button" disabled={busy} className="text-[12px] text-accent hover:underline" onClick={() => writes.clear.mutate({ project_id: p.project_id, ready_key: k }, { onSuccess: onClose })}>Back to automatic</button>}
          <span className="flex-1"/>
          <button type="button" className="text-[12px] text-text-muted hover:text-text px-2 py-1" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ---- readiness squares --------------------------------------------------------

export const READY_CLS: Record<ReadyState, string> = {
  ready: "bg-ok", partial: "bg-warn", pending: "bg-warn", missing: "bg-danger", none: "bg-line", na: "bg-line",
};
export const READY_LABEL: Record<ReadyState, string> = { ready: "ready", partial: "partial", pending: "pending", missing: "missing", none: "not yet", na: "n/a" };

export function ReadinessSquares({ p, size = 10, stacked = false, onPick }: { p: PmoProject; size?: number; stacked?: boolean; onPick?: (k: ReadyKey) => void }) {
  const items: [string, ReadyState, string, ReadyKey][] = [
    ["Design / FC", p.design_state, p.design_why, "design"], ["Materials", p.materials_state, p.materials_why, "materials"],
    ["Outage", p.outage_state, p.outage_why, "outage"], ["Plant", p.plant_state, p.plant_why, "plant"], ["Access", p.access_state, p.access_why, "access"],
    ["PO", p.po_state ?? "na", p.po_why ?? "—", "po"],   // 27 Oct 2026 — James: "add PO to the RAG list"
  ];
  const mark = (k: ReadyKey) => p.ready_overrides?.[k] ? "ring-2 ring-offset-1 ring-offset-ink-card ring-accent/70" : "";
  // 27 Oct 2026 — James, on the PMO delivery table: "since these are pretty
  // much already high lets stack the RAG squares vertically with a small
  // label on what it is". The rows are tall anyway (site name wraps), so the
  // five squares go down the cell with their word beside them; the hover
  // still says why.
  if (stacked) {
    return (
      <span className="inline-flex flex-col gap-[3px]" title={items.map(([l, s, w]) => `${l}: ${READY_LABEL[s]} — ${w}`).join("\n")}>
        {items.map(([l, s, w, k]) => (
          <button type="button" key={l} disabled={!onPick} onClick={e => { e.stopPropagation(); onPick?.(k); }} className={`inline-flex items-center gap-1.5 leading-none text-left ${onPick ? "hover:opacity-80 cursor-pointer" : "cursor-default"}`} title={`${l}: ${READY_LABEL[s]} — ${w}${onPick ? "\n(click to set it in the meeting)" : ""}`}>
            <i className={`inline-block rounded-sm shrink-0 ${READY_CLS[s]} ${mark(k)}`} style={{ width: size, height: size }} aria-label={`${l} ${READY_LABEL[s]}`}/>
            <span className={`text-[10px] ${s === "missing" ? "text-danger" : s === "partial" || s === "pending" ? "text-warn" : s === "ready" ? "text-text-muted" : "text-text-dim"}`}>{l === "Design / FC" ? "Design/FC" : l}</span>
          </button>
        ))}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1" title={items.map(([l, s, w]) => `${l}: ${READY_LABEL[s]} — ${w}`).join("\n")}>
      {items.map(([l, s, , k]) => <i key={l} className={`inline-block rounded-sm ${READY_CLS[s]} ${mark(k)}`} style={{ width: size, height: size }} aria-label={`${l} ${READY_LABEL[s]}`}/>)}
    </span>
  );
}

export function ReadinessList({ p, onPick }: { p: PmoProject; onPick?: (k: ReadyKey) => void }) {
  const items: [string, ReadyState, string, ReadyKey][] = [
    ["Design / FC", p.design_state, p.design_why, "design"], ["Materials", p.materials_state, p.materials_why, "materials"],
    ["Outage", p.outage_state, p.outage_why, "outage"], ["Plant", p.plant_state, p.plant_why, "plant"], ["Access", p.access_state, p.access_why, "access"],
    ["PO", p.po_state ?? "na", p.po_why ?? "—", "po"],   // 27 Oct 2026 — James: "add PO to the RAG list"
  ];
  return (
    <div className="space-y-1.5">
      {items.map(([l, s, w, k]) => (
        <button type="button" key={l} disabled={!onPick} onClick={() => onPick?.(k)} className={`w-full flex items-start gap-2 text-[12px] text-left rounded px-1 -mx-1 ${onPick ? "hover:bg-ink-card/60" : ""}`}>
          <i className={`mt-1 inline-block w-2.5 h-2.5 rounded-sm shrink-0 ${READY_CLS[s]} ${p.ready_overrides?.[k] ? "ring-2 ring-offset-1 ring-offset-ink-raised ring-accent/70" : ""}`}/>
          <span className="w-24 shrink-0 text-text-muted">{l}</span>
          <span className="text-text">{w}</span>
        </button>
      ))}
    </div>
  );
}

/** Rank for "needs a word first": blocked / overdue / missing readiness / HOP waiting on the client first. */
export function attentionScore(p: PmoProject): number {
  let s = 0;
  if (p.standdown_blocks) s += 100;
  s += p.overdue_defects * 10 + p.open_defects * 2;
  if (p.bucket === "upcoming" && (p.design_state === "missing" || p.materials_state === "missing")) s += 25;
  if (p.bucket === "live" && p.po_state === "missing") s += 15;   // working with no paper
  if (p.hop_status && HOP_OUTSTANDING.has(p.hop_status)) s += 8 + Math.min(daysSince(p.hop_submitted_at) ?? 0, 30);
  if (p.pmo_note) s += 5;
  const d = daysUntil(p.scheduled_end_date);
  if (p.bucket === "live" && d != null && d < 0) s += 15;
  if (p.pmo_flag) s += 40;          // mig 491 — flagged by hand
  if (p.date_clash) s += 20;        // mig 491 — read off the dates
  return s;
}
